import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { GET_USERS } from "../../../../../apis/settingsApis/access-apis/accessApis";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import {
  getUniqueSystems,
  transformUserAccessMatrix,
  getUniqueRoles,
} from "../utils/dataTransformers";
import { SystemKey, IntegrationType } from "../../../../../utils/enums";
export const useAccessMatrixData = (filterObject = {}) => {
  const {
    data: usersData,
    loading: loadingUsers,
  } = useQuery(GET_USERS, {
    variables: {
      page: 1,
      limit: 10000,
      filter: filterObject,
    },
    fetchPolicy: "cache-and-network",
  });

  // Fetch staff details for department and role information
  const { data: staffData } = useQuery(GET_STAFF_DETAILS);

  const users = useMemo(() => usersData?.getUsers?.data || [], [usersData]);
  const employees = useMemo(
    () => staffData?.getEmployees?.data || [],
    [staffData]
  );

  // Extract distinct systems from users data for filters
  const systems = useMemo(() => {
    const seenIds = new Set();
    const uniqueSystems = [];
    
    users.forEach((user) => {
      const system = user.system;
      if (system?.id && !seenIds.has(system.id)) {
        // Exclude Zoho People only if it's not manual (allow manual Zoho People)
        const isZohoPeople = system.key?.toLowerCase() === SystemKey?.ZOHO_PEOPLE;
        const isManualZoho = isZohoPeople && (system.integration_type || '').toUpperCase() === IntegrationType.MANUAL;
        if (!isZohoPeople || isManualZoho) {
          seenIds.add(system.id);
          uniqueSystems.push(system);
        }
      }
    });
    
    return uniqueSystems.sort((a, b) => a.id - b.id);
  }, [users]);

  // Get unique systems for table columns (grouped by label, not ID)
  const uniqueSystems = useMemo(
    () => getUniqueSystems(users),
    [users]
  );

  // Transform data: Group by user with system access mapped by system label
  const userAccessMatrix = useMemo(
    () => transformUserAccessMatrix(users, employees),
    [users, employees]
  );

  // Get unique roles for filtering
  const roles = useMemo(
    () => getUniqueRoles(userAccessMatrix),
    [userAccessMatrix]
  );

  const loading = loadingUsers;

  return {
    uniqueSystems,
    userAccessMatrix,
    roles,
    loading,
    systems,
  };
};

